import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Tab,
  Tabs,
  Card,
  CardContent,
  TextField,
  Grid,
  Button,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  FormControl,
  InputLabel,
  TablePagination,
  Dialog,
} from "@mui/material";

import { Frame } from "../../components/layouts/Frame";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface PricePoints {
  area_price_1: number;
  area_price_2: number;
  area_price_3: number;
  area_price_4: number;
  glass: number;
  balustrade: number;
  sub_frame: number;
  gate: number;
  light: number;
  composite_frame: number;
  skirting_price_1: number;
  skirting_price_2: number;
  skirting_price_3: number;
  skirting_price_4: number;
}

const initialPrices: PricePoints = {
  area_price_1: 0,
  area_price_2: 0,
  area_price_3: 0,
  area_price_4: 0,
  glass: 0,
  balustrade: 0,
  sub_frame: 0,
  gate: 0,
  light: 0,
  composite_frame: 0,
  skirting_price_1: 0,
  skirting_price_2: 0,
  skirting_price_3: 0,
  skirting_price_4: 0,
};

const DeckTypeList = [
  ["Plain", "Foiled", "Foil plus", "UPVC "],
  ["ESSENTIALS", "UPVC", "HERITAGE", "EVOLUTION"],
];

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type ColourType = "ecodek" | "deckboard";
type DeckType = "1" | "2" | "3" | "4";
interface Colour {
  id: string;
  name: string;
  deck_type: DeckType;
  type: ColourType;
  is_active: boolean;
}

interface NewColour {
  name: string;
  deck_type: DeckType;
}

export const Settings = () => {
  const { fetchData } = useAPI();
  const { isAuthenticated } = useAuth();

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [ecodekPrices, setEcodekPrices] = useState<PricePoints>(initialPrices);
  const [deckboardPrices, setDeckBoardPrices] =
    useState<PricePoints>(initialPrices);

  const [deckboardColours, setDeckboardColours] = useState<Colour[]>([]);
  const [ecodekColours, setEcodekColours] = useState<Colour[]>([]);
  const [newDeckboardColour, setNewDeckboardColour] = useState<NewColour>({
    name: "",
    deck_type: "1",
  });
  const [newEcodekColour, setNewEcodekColour] = useState<NewColour>({
    name: "",
    deck_type: "1",
  });
  const [ecodekPage, setEcodeckPage] = useState(0);
  const [ecodekRowsPerPage, setEcodekRowsPerPage] = useState(10);

  const [deckboardPage, setDeckboardPage] = useState(0);
  const [deckboardRowsPerPage, setDeckboardRowsPerPage] = useState(10);

  const handleChangeEcodekPage = (event: unknown, newPage: number) => {
    setEcodeckPage(newPage);
  };

  const handleChangeEcodekRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEcodekRowsPerPage(parseInt(event.target.value, 10));
    setEcodeckPage(0);
  };

  const handleChangeDeckboardPage = (event: unknown, newPage: number) => {
    setDeckboardPage(newPage);
  };

  const handleChangeDeckboardRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDeckboardRowsPerPage(parseInt(event.target.value, 10));
    setDeckboardPage(0);
  };

  const handleChangePrice =
    (field: keyof PricePoints, type: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (type == "ecodek") {
        setEcodekPrices(prev => ({
          ...prev,
          [field]: parseFloat(event.target.value) || "",
        }));
      } else {
        setDeckBoardPrices(prev => ({
          ...prev,
          [field]: parseFloat(event.target.value) || "",
        }));
      }
    };

  const fetchSettings = async () => {
    const { data } = await fetchData("/settings/getList");

    if (data) {
      const ecodekPrices = data.filter((itm: any) => itm?.type == "ecodek");
      const deckboardPrices = data.filter(
        (itm: any) => itm?.type == "deckboard",
      );

      setEcodekPrices({
        area_price_1:
          ecodekPrices.find((itm: any) => itm.key === "area_price_1")?.value ??
          "",
        area_price_2:
          ecodekPrices.find((itm: any) => itm.key === "area_price_2")?.value ??
          "",
        area_price_3:
          ecodekPrices.find((itm: any) => itm.key === "area_price_3")?.value ??
          "",
        area_price_4:
          ecodekPrices.find((itm: any) => itm.key === "area_price_4")?.value ??
          "",
        glass:
          ecodekPrices.find((itm: any) => itm.key === "glass")?.value ?? "",
        balustrade:
          ecodekPrices.find((itm: any) => itm.key === "balustrade")?.value ??
          "",
        sub_frame:
          ecodekPrices.find((itm: any) => itm.key === "sub_frame")?.value ?? "",
        gate: ecodekPrices.find((itm: any) => itm.key === "gate")?.value ?? "",
        light:
          ecodekPrices.find((itm: any) => itm.key === "light")?.value ?? "",
        composite_frame:
          ecodekPrices.find((itm: any) => itm.key === "composite_frame")
            ?.value ?? "",
        skirting_price_1:
          ecodekPrices.find((itm: any) => itm.key === "skirting_price_1")
            ?.value ?? "",
        skirting_price_2:
          ecodekPrices.find((itm: any) => itm.key === "skirting_price_2")
            ?.value ?? "",
        skirting_price_3:
          ecodekPrices.find((itm: any) => itm.key === "skirting_price_3")
            ?.value ?? "",
        skirting_price_4:
          ecodekPrices.find((itm: any) => itm.key === "skirting_price_4")
            ?.value ?? "",
      });

      setDeckBoardPrices({
        area_price_1:
          deckboardPrices.find((itm: any) => itm.key === "area_price_1")
            ?.value ?? "",
        area_price_2:
          deckboardPrices.find((itm: any) => itm.key === "area_price_2")
            ?.value ?? "",
        area_price_3:
          deckboardPrices.find((itm: any) => itm.key === "area_price_3")
            ?.value ?? "",
        area_price_4:
          deckboardPrices.find((itm: any) => itm.key === "area_price_4")
            ?.value ?? "",
        glass:
          deckboardPrices.find((itm: any) => itm.key === "glass")?.value ?? "",
        balustrade:
          deckboardPrices.find((itm: any) => itm.key === "balustrade")?.value ??
          "",
        sub_frame:
          deckboardPrices.find((itm: any) => itm.key === "sub_frame")?.value ??
          "",
        gate:
          deckboardPrices.find((itm: any) => itm.key === "gate")?.value ?? "",
        light:
          deckboardPrices.find((itm: any) => itm.key === "light")?.value ?? "",
        composite_frame:
          deckboardPrices.find((itm: any) => itm.key === "composite_frame")
            ?.value ?? "",
        skirting_price_1:
          deckboardPrices.find((itm: any) => itm.key === "skirting_price_1")
            ?.value ?? "",
        skirting_price_2:
          deckboardPrices.find((itm: any) => itm.key === "skirting_price_2")
            ?.value ?? "",
        skirting_price_3:
          deckboardPrices.find((itm: any) => itm.key === "skirting_price_3")
            ?.value ?? "",
        skirting_price_4:
          deckboardPrices.find((itm: any) => itm.key === "skirting_price_4")
            ?.value ?? "",
      });
    }
  };

  const fetchColours = async () => {
    const { data } = await fetchData("/colours/get_list");

    if (data) {
      setDeckboardColours(
        data.filter((itm: Colour) => itm.type === "deckboard"),
      );
      setEcodekColours(data.filter((itm: Colour) => itm.type === "ecodek"));
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
      fetchColours();
    }
  }, [isAuthenticated]);

  const handleSave = async () => {
    const payload = {
      ecodek: ecodekPrices,
      deckboard: deckboardPrices,
    };

    const { errors, data } = await fetchData(
      "/settings/update",
      payload,
      "POST",
    );

    setSnackbar({
      open: true,
      message: "Settings updated successfully",
      severity: "success",
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAddColour = async (e: any) => {
    e.preventDefault();

    const NewColourData = value === 0 ? newDeckboardColour : newEcodekColour;

    try {
      const { data } = await fetchData(
        "/colours",
        {
          name: NewColourData.name,
          deck_type: NewColourData.deck_type,
          type: value === 0 ? "deckboard" : "ecodek",
          is_active: true,
        },
        "POST",
      );

      if (data) {
        if (value === 0) {
          setDeckboardColours(prev => [data, ...prev]);
          setNewDeckboardColour({ name: "", deck_type: "1" });
        } else {
          setEcodekColours(prev => [data, ...prev]);
          setNewEcodekColour({ name: "", deck_type: "1" });
        }
      } else {
        console.error("Failed to add colour");
      }
    } catch (err) {
      console.error("Error adding colour:", err);
    }
  };

  const handleToggleActivate = async (id: string) => {
    try {
      const { data } = await fetchData(
        `/colours/${id}/toggleActivate`,
        {},
        "POST",
      );

      if (data) {
        if (value === 0) {
          setDeckboardColours(prev =>
            prev.map(c =>
              c.id === id ? { ...c, is_active: !c.is_active } : c,
            ),
          );
        } else {
          setEcodekColours(prev =>
            prev.map(c =>
              c.id === id ? { ...c, is_active: !c.is_active } : c,
            ),
          );
        }
      }
    } catch (err) {
      console.error("Failed to deactivate colour:", err);
    }
  };

  const handleEcodekInputChange = (e: any) => {
    const { name, value } = e.target;
    setNewEcodekColour(prev => ({ ...prev, [name]: value }));
  };

  const handleDeckboardInputChange = (e: any) => {
    const { name, value } = e.target;
    setNewDeckboardColour(prev => ({ ...prev, [name]: value }));
  };

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Colour | null>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null);

  const openEditDialog = (colour: Colour) => {
    setEditTarget(colour);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;

    const { data } = await fetchData(
      "/colours",
      {
        id: editTarget.id,
        name: editTarget.name,
        deck_type: editTarget.deck_type,
      },
      "POST",
    );

    if (data) {
      if (value === 0) {
        setDeckboardColours(prev =>
          prev.map(c => (c.id === editTarget.id ? data : c)),
        );
      } else {
        setEcodekColours(prev =>
          prev.map(c => (c.id === editTarget.id ? data : c)),
        );
      }

      setEditDialogOpen(false);
    }
  };

  const openConfirmDialog = (id: string) => {
    setConfirmTargetId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    if (confirmTargetId == null) return;

    const { data } = await fetchData(`/colours/${confirmTargetId}/delete`);

    if (data) {
      if (value === 0) {
        setDeckboardColours(prev => prev.filter(c => c.id !== confirmTargetId));
      } else {
        setEcodekColours(prev => prev.filter(c => c.id !== confirmTargetId));
      }

      setConfirmDialogOpen(false);
    }
  };

  return (
    <Frame title="Settings">
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example">
            <Tab
              label="Dekboard"
              {...a11yProps(0)}
              sx={{
                textTransform: "capitalize",
              }}
            />
            <Tab
              label="EcoDek"
              {...a11yProps(1)}
              sx={{
                textTransform: "capitalize",
              }}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <Typography variant="h5" mt={2}>
            Manage Prices
          </Typography>

          <Card sx={{ mx: "auto", mt: 5 }}>
            <CardContent>
              <Grid container spacing={2}>
                {Object.keys(ecodekPrices).map(key => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <TextField
                      label={key.replace(/_/g, " ").toUpperCase()}
                      type="number"
                      value={ecodekPrices[key as keyof PricePoints]}
                      onChange={handleChangePrice(
                        key as keyof PricePoints,
                        "ecodek",
                      )}
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mt: 3 }}>
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Typography variant="h5" mt={2}>
            Manage Colours
          </Typography>

          <Paper sx={{ paddingY: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Add New Colour
            </Typography>

            <Box
              component="form"
              onSubmit={handleAddColour}
              sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ width: 150 }}>
                <Select
                  name="deck_type"
                  value={newDeckboardColour.deck_type}
                  variant="outlined"
                  onChange={handleDeckboardInputChange}>
                  {DeckTypeList[value].map((itm, index) => (
                    <MenuItem value={index + 1}>{itm}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                name="name"
                label="Colour Name"
                value={newDeckboardColour.name}
                onChange={handleDeckboardInputChange}
                required
              />

              <Button type="submit" variant="contained" color="primary">
                Add Colour
              </Button>
            </Box>
          </Paper>

          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Decking Type</TableCell>
                  <TableCell>Colour Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deckboardColours.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        There are no colours.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  deckboardColours
                    .slice(
                      deckboardPage * deckboardRowsPerPage,
                      deckboardPage * deckboardRowsPerPage +
                        deckboardRowsPerPage,
                    )
                    .map(c => (
                      <TableRow key={c.id}>
                        <TableCell>{c.id}</TableCell>
                        <TableCell>
                          {DeckTypeList[value][Number(c.deck_type) - 1]}
                        </TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          {c.is_active ? "Active" : "Inactive"}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleToggleActivate(c.id)}>
                              {c.is_active ? "Deactive" : "Active"}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="info"
                              onClick={() => openEditDialog(c)}>
                              Edit
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => openConfirmDialog(c.id)}>
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={deckboardColours.length}
              page={deckboardPage}
              onPageChange={handleChangeDeckboardPage}
              rowsPerPage={deckboardRowsPerPage}
              onRowsPerPageChange={handleChangeDeckboardRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Typography variant="h5" mt={2}>
            Manage Prices
          </Typography>
          <Card sx={{ mx: "auto", mt: 5 }}>
            <CardContent>
              <Grid container spacing={2}>
                {Object.keys(deckboardPrices).map(key => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <TextField
                      label={key.replace(/_/g, " ").toUpperCase()}
                      type="number"
                      value={deckboardPrices[key as keyof PricePoints]}
                      onChange={handleChangePrice(
                        key as keyof PricePoints,
                        "deckboard",
                      )}
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mt: 3 }}>
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Typography variant="h5" mt={2}>
            Manage Colours
          </Typography>

          <Paper sx={{ paddingY: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Add New Colour
            </Typography>

            <Box
              component="form"
              onSubmit={handleAddColour}
              sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ width: 150 }}>
                <Select
                  name="deck_type"
                  value={newEcodekColour.deck_type}
                  onChange={handleEcodekInputChange}>
                  {DeckTypeList[value].map((itm, index) => (
                    <MenuItem value={index + 1}>{itm}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                name="name"
                label="Colour Name"
                value={newEcodekColour.name}
                onChange={handleEcodekInputChange}
                required
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="small">
                Add Colour
              </Button>
            </Box>
          </Paper>

          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Decking Type</TableCell>
                  <TableCell>Colour Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ecodekColours.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        There are no colours.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ecodekColours
                    .slice(
                      ecodekPage * ecodekRowsPerPage,
                      ecodekPage * ecodekRowsPerPage + ecodekRowsPerPage,
                    )
                    .map(c => (
                      <TableRow key={c.id}>
                        <TableCell>{c.id}</TableCell>
                        <TableCell>
                          {DeckTypeList[value][Number(c.deck_type) - 1]}
                        </TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          {c.is_active ? "Active" : "Inactive"}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleToggleActivate(c.id)}>
                              {c.is_active ? "Deactive" : "Active"}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="info"
                              onClick={() => openEditDialog(c)}>
                              Edit
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => openConfirmDialog(c.id)}>
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={ecodekColours.length}
              page={ecodekPage}
              onPageChange={handleChangeEcodekPage}
              rowsPerPage={ecodekRowsPerPage}
              onRowsPerPageChange={handleChangeEcodekRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        </CustomTabPanel>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          fullWidth>
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Edit Colour
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Deck Type</InputLabel>
                <Select
                  name="deck_type"
                  value={editTarget?.deck_type || ""}
                  onChange={e =>
                    setEditTarget(
                      prev =>
                        prev && {
                          ...prev,
                          deck_type: e.target.value as DeckType,
                        },
                    )
                  }
                  label="Deck Type">
                  {DeckTypeList[value].map((itm, index) => (
                    <MenuItem value={index + 1}>{itm}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Colour Name"
                value={editTarget?.name || ""}
                onChange={e =>
                  setEditTarget(
                    prev => prev && { ...prev, name: e.target.value },
                  )
                }
                required
              />

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleEditSave} variant="contained">
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </Dialog>

        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}>
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Confirm Deletion
            </Typography>
            <Typography gutterBottom>
              Are you sure you want to delete this colour?
            </Typography>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={() => setConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                color="warning"
                variant="contained"
                onClick={handleConfirmDeactivate}>
                Yes
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Frame>
  );
};
